// Reusable responsive grid of StreamCards with staggered entrance, skeleton
// loading, and empty / error states. Shared by Home (featured) and Browse.
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Radio } from "lucide-react";
import type { StreamCardDTO } from "../api/types.js";
import { staggerContainer } from "@/lib/motion";
import { Skeleton } from "./ui/skeleton.js";
import { StreamCard } from "./StreamCard.js";

const GRID = "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4";

export interface StreamGridProps {
  items: StreamCardDTO[];
  isLoading?: boolean;
  isError?: boolean;
  skeletonCount?: number;
  emptyTitle?: string;
  emptyHint?: string;
}

export function StreamGrid({
  items,
  isLoading,
  isError,
  skeletonCount = 8,
  emptyTitle,
  emptyHint,
}: StreamGridProps) {
  const { t } = useTranslation();
  if (isLoading) {
    return (
      <div className={GRID}>
        {Array.from({ length: skeletonCount }).map((_, i) => (
          <div
            key={i}
            className="overflow-hidden rounded-2xl border border-white/[0.06] bg-card/40"
          >
            <Skeleton className="aspect-video w-full rounded-none" />
            <div className="flex items-center gap-2.5 p-3">
              <Skeleton className="h-[34px] w-[34px] rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-3 w-3/4" />
                <Skeleton className="h-2.5 w-1/2" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-2xl border border-white/[0.07] bg-card/60 p-10 text-center text-muted-foreground">
        {t("grid.loadError")}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="surface flex flex-col items-center px-6 py-16 text-center">
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/15 text-primary">
          <Radio className="h-6 w-6" />
        </div>
        <p className="text-lg font-medium text-foreground">{emptyTitle ?? t("grid.emptyTitle")}</p>
        <p className="mt-1 text-sm text-muted-foreground">{emptyHint ?? t("grid.emptyHint")}</p>
      </div>
    );
  }

  return (
    <motion.div
      variants={staggerContainer}
      initial="initial"
      animate="enter"
      className={GRID}
    >
      {items.map((card) => (
        <StreamCard key={card.uuid} card={card} />
      ))}
    </motion.div>
  );
}
